// La sesión: quién soy (/me), mi rol, lo que puedo hacer, y el cliente en curso.
// Sin sesión válida (401) se va a la página de acceso; nunca una pantalla en blanco.
import { createContext, useCallback, useContext, type ReactNode } from "react";
import { useNavigate } from "@tanstack/react-router";
import { ApiError } from "@responia/api/cliente";
import type { Me } from "@responia/api/esquemas";
import { puede, type Accion } from "@responia/api/permisos";
import { useLectura } from "./datos";
import { useDirecto, type EstadoDirecto } from "./directo";

type Sesion = { me: Me; puede: (a: Accion) => boolean; directo: EstadoDirecto };
const Ctx = createContext<Sesion | null>(null);

export function SesionProvider({ children, cargando, error }: {
  children: ReactNode; cargando: ReactNode; error: (codigo: string, ref?: string) => ReactNode;
}) {
  const me = useLectura("/me");
  const navegar = useNavigate();
  const salir = useCallback(() => { navegar({ to: "/acceso" }); }, [navegar]);
  const directo = useDirecto(me.isSuccess, salir);

  if (me.error instanceof ApiError && me.error.estado === 401) {
    queueMicrotask(salir);
    return <>{cargando}</>;
  }
  if (me.isError) {
    const e = me.error instanceof ApiError ? me.error : new ApiError(0, "interno");
    return <>{error(e.codigo, e.ref)}</>;
  }
  if (!me.data) return <>{cargando}</>;
  const datos = me.data;
  return <Ctx.Provider value={{ me: datos, puede: (a) => puede(datos.rol, a), directo }}>{children}</Ctx.Provider>;
}

export function useSesion(): Sesion {
  const v = useContext(Ctx);
  if (!v) throw new Error("useSesion fuera de SesionProvider");
  return v;
}
